import { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardHeader,
    Box,
    Button,
    Stack,
    ListItemButton,
    ListItemText,
    Chip,
    Typography,
    Skeleton,
} from '@mui/material';
import { softCardSx } from '../constants';
import { formatLimitDate, isOverdue } from '@/modules/cards/cardStatus';
import type { CardMineSummary, CardSummaryItem } from '@/types';

interface MyCardsWidgetProps {
    enabled: boolean;
}

export const MyCardsWidget: React.FC<MyCardsWidgetProps> = ({ enabled }) => {
    const navigate = useNavigate();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [data, setData] = useState<{
        pending: CardSummaryItem[];
        working: CardSummaryItem[];
        pending_count: number;
        working_count: number;
    } | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setData(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        dataProvider.getMyCardSummary()
            .then((result: CardMineSummary) => {
                if (!cancelled) {
                    setData(result);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    const message = err instanceof Error ? err.message : 'Failed to load cards';
                    notify(message, { type: 'error' });
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, notify, dataProvider]);

    if (!enabled) return null;

    if (loading) {
        return (
            <Card sx={softCardSx}>
                <CardHeader title="My Jobs" />
                <CardContent>
                    <Stack spacing={1}>
                        {[1, 2, 3].map(i => (
                            <Box key={i}>
                                <Skeleton variant="text" height={40} data-testid="Skeleton" />
                            </Box>
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    if (!data) {
        return (
            <Card sx={softCardSx}>
                <CardHeader title="My Jobs" />
                <CardContent>
                    <Typography color="textSecondary">Failed to load cards</Typography>
                </CardContent>
            </Card>
        );
    }

    const hasCards = data.pending.length > 0 || data.working.length > 0;

    const renderCardRow = (card: CardSummaryItem) => (
        <ListItemButton
            key={card.id}
            onClick={() => navigate(`/cards/${card.id}/show`)}
            sx={{ mb: 1 }}
        >
            <ListItemText
                primary={card.title}
                secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                        {card.assigned_to_name && (
                            <Typography variant="caption" color="textSecondary">
                                {card.assigned_to_name}
                            </Typography>
                        )}
                        {card.limit_date && (
                            <Chip
                                label={formatLimitDate(card.limit_date)}
                                size="small"
                                color={isOverdue(card.limit_date, card.status) ? 'error' : 'default'}
                                variant={isOverdue(card.limit_date, card.status) ? 'filled' : 'outlined'}
                            />
                        )}
                    </Box>
                }
            />
        </ListItemButton>
    );

    return (
        <Card sx={softCardSx}>
            <CardHeader
                title="My Jobs"
                action={
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate('/cards')}
                    >
                        View board
                    </Button>
                }
            />
            <CardContent>
                {!hasCards ? (
                    <Typography color="textSecondary">Nothing assigned to you</Typography>
                ) : (
                    <Stack spacing={2}>
                        {data.working.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Working ({data.working_count})
                                </Typography>
                                <Stack spacing={0.5}>
                                    {data.working.map(renderCardRow)}
                                </Stack>
                            </Box>
                        )}

                        {data.pending.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Pending ({data.pending_count})
                                </Typography>
                                <Stack spacing={0.5}>
                                    {data.pending.map(renderCardRow)}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

MyCardsWidget.displayName = 'MyCardsWidget';
